import PropTypes from 'prop-types';
import { Link, useLocation, matchPath } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import IconButton from 'components/@extended/IconButton';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

export default function NavItem({ item, level, isParents = false, setSelectedID }) {
  const theme = useTheme();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((t) => t.breakpoints.down('lg'));

  const { pathname } = useLocation();
  const href = item?.link ? item.link : item.url;
  const isSelected = !!matchPath({ path: href, end: false }, pathname);

  const itemTarget = item.target ? '_blank' : '_self';
  const Icon = item.icon;

  const onClick = () => {
    if (downLG) handlerDrawerOpen(false);
    if (isParents && setSelectedID) setSelectedID(item.id);
  };

  const railMode = !drawerOpen && level === 1;

  return (
    <ListItemButton
      component={Link}
      to={href}
      target={itemTarget}
      disabled={item.disabled}
      selected={isSelected}
      onClick={onClick}
      sx={{
        my: 0.2,
        mx: railMode ? 0.5 : 0,
        px: railMode ? 0 : 1,
        py: railMode ? 1 : 1.2,
        borderRadius: '8px',
        justifyContent: 'center',
        color: isSelected ? '#667eea' : 'text.primary',
        position: 'relative',
        transition: 'all 0.2s ease',
        pl: isSelected ? '5px' : undefined,

        '&:hover': {
          bgcolor: isSelected
            ? 'rgba(102, 126, 234, 0.08)'
            : theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.08)'
              : 'rgba(0, 0, 0, 0.04)',
          '& .nav-icon': {
            color: '#667eea'
          }
        },
        '&.Mui-selected': {
          bgcolor: 'rgba(102, 126, 234, 0.08)',
          color: '#667eea',
          fontWeight: 600,
          '& .nav-icon': {
            color: '#667eea'
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '3px',
            borderRadius: '0 8px 8px 0',
            background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)'
          }
        }
      }}
    >
      {railMode ? (
        <Box sx={{ width: '100%', display: 'grid', justifyItems: 'center', rowGap: 0.75 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '999px',
              display: 'grid',
              placeItems: 'center',
              bgcolor: isSelected ? (theme.palette.mode === 'dark' ? '#a898cc17' : '#a898cc17') : 'transparent'
            }}
          >
            {Icon ? <Icon style={{ fontSize: 25 }} /> : null}
          </Box>

          <Typography
            variant="caption"
            noWrap
            sx={{
              width: 70,
              textAlign: 'center',
              fontSize: '0.625rem',
              lineHeight: '14px',
              color: theme.palette.text.primary
            }}
          >
            {item.title}
          </Typography>
        </Box>
      ) : (
        // Normal drawer (open): classic row layout
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1, pt: 0.5 }}>
          {Icon ? (
            <Box
              className="nav-icon"
              sx={{
                width: 34,
                display: 'grid',
                placeItems: 'center',
                color: isSelected ? '#667eea' : 'text.secondary',
                transition: 'color 0.2s'
              }}
            >
              <Icon style={{ fontSize: 20 }} />
            </Box>
          ) : null}

          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: isSelected ? 600 : 500,
              color: isSelected ? '#667eea' : 'inherit',
              display: 'block',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              lineHeight: 1.3
            }}
          >
            {item.title.startsWith('AI ') ? (
              <>
                <span
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 700
                  }}
                >
                  AI
                </span>
                {' ' + item.title.substring(2)}
              </>
            ) : (
              item.title
            )}
          </Typography>

          {/* optional actions for open mode only (same as your pattern) */}
          {item?.actions?.map((action, index) => {
            const ActionIcon = action.icon;
            const callAction = action?.function;
            return (
              <IconButton
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  if (action.type === 'button') callAction?.();
                }}
                sx={{ ml: 'auto' }}
              >
                {ActionIcon ? <ActionIcon style={{ fontSize: 18 }} /> : null}
              </IconButton>
            );
          })}
        </Box>
      )}
    </ListItemButton>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.func])
};
